import React, { useState, lazy } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SearchIcon, MailIcon, BarChart3Icon, CheckCircle2Icon, ArrowRightIcon } from 'lucide-react';
const tabs = [{
  id: 'find',
  icon: SearchIcon,
  title: 'Find & enrich local leads',
  description: 'Stop juggling multiple tools. Use our built-in database to find local businesses and instantly enrich them with verified owner emails and phone numbers.'
}, {
  id: 'build',
  icon: MailIcon,
  title: 'Build B2B email sequences',
  description: 'Create multi-touch email sequence tools with advanced personalization. Use dynamic variables like company name, tech stack, and location to increase reply rates.'
}, {
  id: 'track',
  icon: BarChart3Icon,
  title: 'Track email sequence performance',
  description: 'Track how your email sequences are performing with built-in analytics. See opens, clicks, replies, and follow-up performance in one place, so you know which messages are working and where prospects are dropping off.'
}];
export function SequenceWorkflow() {
  const [activeTab, setActiveTab] = useState(tabs[0].id);
  return <section className="py-24 bg-white" data-id="element-1099">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1100">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1101">
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 leading-tight" data-id="element-1102">
            How our{' '}
            <span className="text-blue-600" data-id="element-1103">email sequence software</span> works
          </h2>
          <p className="text-xl text-slate-600 leading-relaxed" data-id="element-1104">
            The only platform that combines local business prospecting with
            automated outreach.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 items-center" data-id="element-1105">
          {/* Left: Tabs */}
          <div className="lg:col-span-5 space-y-2" data-id="element-1106">
            {tabs.map(tab => {
            const isActive = activeTab === tab.id;
            return <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`w-full text-left p-8 rounded-[2rem] transition-all duration-300 ${isActive ? 'bg-slate-50' : 'bg-white hover:bg-slate-50/50'}`} data-id="element-1107">
                  <div className="flex items-start gap-6" data-id="element-1108">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${isActive ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-400'}`} data-id="element-1109">
                      <tab.icon className="w-6 h-6" data-id="element-1110" />
                    </div>
                    <div data-id="element-1111">
                      <h3 className={`text-xl font-bold mb-3 transition-colors ${isActive ? 'text-slate-900' : 'text-slate-700'}`} data-id="element-1112">
                        {tab.title}
                      </h3>
                      <p className={`text-base leading-relaxed transition-colors ${isActive ? 'text-slate-600' : 'text-slate-500'}`} data-id="element-1113">
                        {tab.description}
                      </p>
                    </div>
                  </div>
                </button>;
          })}
          </div>

          {/* Right: Dynamic Visuals */}
          <div className="lg:col-span-7" data-id="element-1114">
            <div className="bg-slate-50 rounded-[2.5rem] p-6 md:p-10 min-h-[450px] flex items-center justify-center relative overflow-hidden border border-slate-100" data-id="element-1115">
              <AnimatePresence mode="wait" data-id="element-1116">
                {activeTab === 'find' && <motion.div key="find" initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.2
              }} className="w-full max-w-md bg-white rounded-3xl p-6 shadow-sm border border-slate-100" data-id="element-1117">
                    <div className="flex items-center justify-between mb-6 pb-4 border-b border-slate-100" data-id="element-1118">
                      <div className="font-bold text-slate-900" data-id="element-1119">
                        Prospect List
                      </div>
                      <div className="text-xs font-semibold text-blue-600 bg-blue-50 px-2 py-1 rounded-md" data-id="element-1120">
                        245 Leads Found
                      </div>
                    </div>
                    <div className="space-y-4" data-id="element-1121">
                      {[1, 2, 3].map(i => <div key={i} className="flex items-center justify-between p-3 rounded-xl bg-slate-50 border border-slate-100" data-id="element-1122">
                          <div className="flex items-center gap-3" data-id="element-1123">
                            <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center" data-id="element-1124">
                              <SearchIcon className="w-4 h-4 text-slate-500" data-id="element-1125" />
                            </div>
                            <div data-id="element-1126">
                              <div className="text-sm font-bold text-slate-900" data-id="element-1127">
                                Local Restaurant {i}
                              </div>
                              <div className="text-xs text-slate-500" data-id="element-1128">
                                owner{i}@restaurant.com
                              </div>
                            </div>
                          </div>
                          <CheckCircle2Icon className="w-5 h-5 text-green-500" data-id="element-1129" />
                        </div>)}
                    </div>
                  </motion.div>}

                {activeTab === 'build' && <motion.div key="build" initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.2
              }} className="w-full max-w-lg bg-white rounded-3xl p-6 shadow-sm border border-slate-100" data-id="element-1130">
                    <div className="mb-4" data-id="element-1131">
                      <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1" data-id="element-1132">
                        Subject
                      </div>
                      <div className="text-sm font-semibold text-slate-900 p-2 bg-slate-50 rounded-lg border border-slate-100" data-id="element-1133">
                        Question about {'{{companyName}}'}
                      </div>
                    </div>
                    <div data-id="element-1134">
                      <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1" data-id="element-1135">
                        Body
                      </div>
                      <div className="text-sm text-slate-700 p-4 bg-slate-50 rounded-lg border border-slate-100 leading-relaxed" data-id="element-1136">
                        Hi {'{{firstName}}'},<br data-id="element-1137" />
                        <br data-id="element-1138" />I noticed that {'{{companyName}}'} is currently
                        using {'{{techStack}}'} in {'{{location}}'}.<br data-id="element-1139" />
                        <br data-id="element-1140" />
                        We help businesses like yours streamline operations.
                        Open to a quick chat?
                      </div>
                    </div>
                  </motion.div>}

                {activeTab === 'track' && <motion.div key="track" initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.2
              }} className="w-full max-w-lg bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden" data-id="element-1141">
                    {/* Metric Row */}
                    <div className="grid grid-cols-5 divide-x divide-slate-100 border-b border-slate-100" data-id="element-1142">
                      {[{
                    label: 'Contacts',
                    value: '3,540',
                    change: '+120'
                  }, {
                    label: 'Opened',
                    value: '42.8%',
                    change: '+5.2%'
                  }, {
                    label: 'Clicked',
                    value: '18.3%',
                    change: '+2.1%'
                  }, {
                    label: 'Replied',
                    value: '12.4%',
                    change: '+1.1%'
                  }, {
                    label: 'Finished',
                    value: '1,200',
                    change: '+340'
                  }].map((m, i) => <motion.div key={i} initial={{
                    opacity: 0,
                    y: 6
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} transition={{
                    delay: i * 0.06,
                    duration: 0.3
                  }} className="px-3 py-3 text-center" data-id="element-1143">
                          <div className="text-[9px] text-slate-400 font-medium" data-id="element-1144">
                            {m.label}
                          </div>
                          <div className="text-sm font-bold text-slate-900 mt-0.5" data-id="element-1145">
                            {m.value}
                          </div>
                          <div className="text-[8px] text-green-600 font-medium" data-id="element-1146">
                            {m.change}
                          </div>
                        </motion.div>)}
                    </div>

                    {/* Mini Bar Chart */}
                    <div className="px-5 py-4 border-b border-slate-100" data-id="element-1147">
                      <div className="flex items-center justify-between mb-2" data-id="element-1148">
                        <span className="text-[10px] font-semibold text-slate-700" data-id="element-1149">
                          Email Schedule
                        </span>
                        <div className="flex gap-2 text-[8px] text-slate-400" data-id="element-1150">
                          <span className="flex items-center gap-1" data-id="element-1151">
                            <span className="w-1.5 h-1.5 rounded-sm bg-blue-400 inline-block" data-id="element-1152"></span>
                            Sent
                          </span>
                          <span className="flex items-center gap-1" data-id="element-1153">
                            <span className="w-1.5 h-1.5 rounded-sm bg-amber-300 inline-block" data-id="element-1154"></span>
                            Scheduled
                          </span>
                        </div>
                      </div>
                      <div className="flex items-end gap-1.5 h-16" data-id="element-1155">
                        {[{
                      s: 35,
                      sc: 20
                    }, {
                      s: 65,
                      sc: 30
                    }, {
                      s: 80,
                      sc: 25
                    }, {
                      s: 10,
                      sc: 40
                    }, {
                      s: 45,
                      sc: 50
                    }, {
                      s: 70,
                      sc: 55
                    }, {
                      s: 60,
                      sc: 45
                    }].map((bar, i) => <div key={i} className="flex-1 flex gap-px items-end justify-center" style={{
                      height: '100%'
                    }} data-id="element-1156">
                            <motion.div initial={{
                        height: 0
                      }} animate={{
                        height: `${bar.s}%`
                      }} transition={{
                        delay: 0.2 + i * 0.04,
                        duration: 0.4,
                        ease: 'easeOut'
                      }} className="w-full bg-blue-400 rounded-t-sm" data-id="element-1157" />
                            <motion.div initial={{
                        height: 0
                      }} animate={{
                        height: `${bar.sc}%`
                      }} transition={{
                        delay: 0.3 + i * 0.04,
                        duration: 0.4,
                        ease: 'easeOut'
                      }} className="w-full bg-amber-300 rounded-t-sm" data-id="element-1158" />
                          </div>)}
                      </div>
                      <div className="flex gap-1.5 mt-1" data-id="element-1159">
                        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(d => <div key={d} className="flex-1 text-center text-[7px] text-slate-400" data-id="element-1160">
                              {d}
                            </div>)}
                      </div>
                    </div>

                    {/* Sequence Table */}
                    <div className="px-5 py-3" data-id="element-1161">
                      <div className="grid grid-cols-6 text-[8px] font-bold text-slate-400 uppercase tracking-wider mb-2" data-id="element-1162">
                        <span className="col-span-2" data-id="element-1163">Sequence</span>
                        <span className="text-center" data-id="element-1164">Contacts</span>
                        <span className="text-center" data-id="element-1165">Opened</span>
                        <span className="text-center" data-id="element-1166">Replied</span>
                        <span className="text-center" data-id="element-1167">Interested</span>
                      </div>
                      {[{
                    name: 'Q3 Dental Outreach',
                    contacts: 450,
                    opened: 216,
                    replied: 63,
                    interested: 28
                  }, {
                    name: 'Austin Coffee Promo',
                    contacts: 320,
                    opened: 145,
                    replied: 41,
                    interested: 15
                  }].map((seq, i) => <motion.div key={i} initial={{
                    opacity: 0
                  }} animate={{
                    opacity: 1
                  }} transition={{
                    delay: 0.5 + i * 0.1
                  }} className="grid grid-cols-6 text-[10px] py-2 border-t border-slate-50" data-id="element-1168">
                          <span className="col-span-2 font-semibold text-slate-800 truncate pr-2" data-id="element-1169">
                            {seq.name}
                          </span>
                          <span className="text-center text-slate-600" data-id="element-1170">
                            {seq.contacts}
                          </span>
                          <span className="text-center text-slate-600" data-id="element-1171">
                            {seq.opened}
                          </span>
                          <span className="text-center text-slate-600" data-id="element-1172">
                            {seq.replied}
                          </span>
                          <span className="text-center text-slate-600" data-id="element-1173">
                            {seq.interested}
                          </span>
                        </motion.div>)}
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </section>;
}